import { type ExtractRefType, Icon_Checkmark24 } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";

import { Icon_FillerSvg24 } from "./FillerSvg";
import { MenuPortal } from "./MenuPortal";
import { SelectActivator } from "./SelectActivator";
import { SingleIconLabel } from "./SingleIconLabel";

import styles from "./RoleSelector.module.css";

interface RoleSelectorProps<TRoles extends readonly [string, ...string[]]> {
  menuRef: React.MutableRefObject<ExtractRefType<typeof MenuPortal> | null>;
  isPending?: boolean;
  roles: TRoles;
  roleWatch: TRoles[number];
  isRequestAccess?: boolean;
  onClick?: (role: TRoles[number]) => void;
  onRemove?: () => void;
  roleDisplayName: (role: TRoles[number]) => string;
  removeActionText?: string;
}

export function RoleSelector<TRoles extends readonly [string, ...string[]]>({
  menuRef,
  isPending,
  roleDisplayName,
  roles,
  roleWatch,
  onClick,
  onRemove,
  isRequestAccess = false,
  removeActionText = "Remove...",
}: RoleSelectorProps<TRoles>) {
  return (
    <MenuPortal
      dropdownPosition="left"
      ref={menuRef}
      activator={
        isRequestAccess ? (
          <div
            role="button"
            tabIndex={0}
            className={classNames(styles.requestAccess, isPending && styles.disabled)}
          >
            Review Access Request...
          </div>
        ) : (
          <SelectActivator disabled={isPending} label={roleDisplayName(roleWatch)} />
        )
      }
    >
      {roles.map((role) => (
        <MenuPortal.Item
          key={role}
          closeOnClick
          onClick={(event) => {
            event.preventDefault();
            onClick?.(role);
          }}
        >
          <SingleIconLabel
            icon={role === roleWatch ? <Icon_Checkmark24 /> : <Icon_FillerSvg24 />}
            label={roleDisplayName(role)}
          />
        </MenuPortal.Item>
      ))}
      <MenuPortal.Divider />
      <MenuPortal.Item
        closeOnClick
        onClick={async (event) => {
          event.preventDefault();
          onRemove?.();
        }}
      >
        <span className={styles.red}>{removeActionText}</span>
      </MenuPortal.Item>
    </MenuPortal>
  );
}
